import { useState } from 'react';
import { Card } from '../../components/Card';
import { Checkbox, IconButton, Pill, TextInput } from '../../components/ui';
import { clsx } from '../../components/clsx';
import { MAX_TODO_TEXT, type Todo } from '../../lib/dailies';
import { useTodos } from './useTodos';

/**
 * 🎯 Aspirations & Todos.
 *
 * Today's secondary checklist. Resets at local midnight via `useDailyState`.
 * Entirely independent of the top bar's One Priority field — different key,
 * different owner, no shared state (spec: "No connection to the top bar One
 * Priority field").
 *
 * One of the two panels permitted an internal scroll region (the list holds
 * unbounded user data), so the card is bounded and the <ul> scrolls; the add
 * form is pinned in the Card footer and never scrolls away.
 */
export function TodosPanel() {
  const { items, counts, add, toggle, remove, clearDone } = useTodos();
  const [draft, setDraft] = useState('');

  const submit = () => {
    add(draft);
    setDraft('');
  };

  return (
    <Card
      title="Aspirations & Todos"
      icon="🎯"
      accent="todo"
      testId="todos-panel"
      scroll
      status={counts.total === 0 ? 'empty' : 'ready'}
      emptyMessage="Nothing on today's list."
      hint="Add what you want to get done — it clears itself at midnight."
      badge={
        counts.total > 0 ? (
          <Pill tone={counts.done === counts.total ? 'positive' : 'neutral'}>
            {counts.done}/{counts.total}
          </Pill>
        ) : undefined
      }
      action={
        counts.done > 0 ? (
          <IconButton label="Clear completed todos" onClick={clearDone}>
            <span aria-hidden className="text-xs">
              ⌫
            </span>
          </IconButton>
        ) : undefined
      }
      footer={
        <div className="flex items-center gap-1.5">
          <TextInput
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            maxLength={MAX_TODO_TEXT}
            placeholder="Add an aspiration…"
            aria-label="New todo"
            className="h-7 flex-1 py-0"
          />
          <IconButton label="Add todo" onClick={submit} disabled={draft.trim() === ''}>
            <span aria-hidden className="text-base leading-none">
              +
            </span>
          </IconButton>
        </div>
      }
    >
      <ul className="flex flex-col gap-0.5 pt-0.5">
        {items.map((todo) => (
          <TodoRow key={todo.id} todo={todo} onToggle={toggle} onRemove={remove} />
        ))}
      </ul>
    </Card>
  );
}

function TodoRow({
  todo,
  onToggle,
  onRemove,
}: {
  todo: Todo;
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <li
      data-testid="todo-row"
      data-done={todo.done ? 'true' : 'false'}
      className="group flex items-center gap-2 py-1"
    >
      <Checkbox checked={todo.done} onChange={() => onToggle(todo.id)} label={todo.text} />
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        onClick={() => onToggle(todo.id)}
        className={clsx(
          'min-w-0 flex-1 truncate text-left text-[13px] transition-colors',
          todo.done ? 'text-ink-subtle line-through' : 'text-ink',
        )}
      >
        {todo.text}
      </button>
      <IconButton
        label={`Remove ${todo.text}`}
        onClick={() => onRemove(todo.id)}
        className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
      >
        <span aria-hidden className="text-xs">
          ✕
        </span>
      </IconButton>
    </li>
  );
}
